<i18n>
en:
  search: Search
  dashboard: Dashboard
  monitors: Monitors
  outages: Outages
  reports: Reports
  status_page: Status Page
  account: Account
  close: Close
  add_monitor: Add Monitor
  feedback: Feedback
  settings: Settings
  logout: Logout {name}
  newVersionAvailable: A new version is available.
  reload: Reload
es:
  search: Buscar
  dashboard: Escritorio
  monitors: Monitores
  outages: Tiempo de Inactividad
  reports: "Reportes"
  status_page: "Status Page" #DTR
  account: Cuenta
  close: Cerrar
  add_monitor: Agregar nuevo Monitor
  feedback: Sugerencias
  settings: Ajustes
  logout: Cerrar sesión
  newVersionAvailable: Una nueva version esta disponible.
  reload: Recargar
</i18n>

<template>
    <v-app :class="appClass">
        <v-app-bar v-if="isDesktop" app>
            <v-app-bar-nav-icon>
                <template v-slot:default>
                    <router-link :to="{name: 'dashboard'}"><v-img
                        :width="36"
                        :height="36"
                        :contain="true"
                        :src="require('@/assets/logo-white.png')"
                        alt="Monitive"
                    /></router-link>
                </template>
            </v-app-bar-nav-icon>
            <v-toolbar-title>Monitive
                <v-chip class="ml-2" color="amber" v-if="isImpersonated">
                    Impersonating {{ firstName }}
                </v-chip>
                <v-chip class="ml-2" color="amber" v-if="showDemo">
                    Demo Account
                </v-chip>
            </v-toolbar-title>
            <v-spacer></v-spacer>

            <v-btn
                v-for="(item, index) in navActions"
                v-bind:key="index"
                icon
            >
                <v-icon @click="item.action">{{ item.icon }}</v-icon>
            </v-btn>

            <v-menu bottom left v-if="contextualMenu">
                <template v-slot:activator="{ on }">
                    <v-btn v-if="isMobile" icon v-on="on">
                        <v-icon>{{ contextualMenu.icon }}</v-icon>
                    </v-btn>
                    <v-btn v-if="isDesktop" v-on="on" large>
                        <v-icon left>{{ contextualMenu.icon }}</v-icon>
                        {{ contextualMenu.label }}
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item
                        v-for="(item, i) in contextualMenu.items"
                        :key="i"
                        @click="item.action(item.params)"
                    >
                        <v-list-item-title>{{ item.label }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <template v-slot:extension>
                <v-tabs
                    align-with-title
                    background-color="transparent"
                >
                    <v-tab
                        :to="{ name: 'dashboard' }"
                    >{{ $t('dashboard') }}</v-tab>
                    <v-tab
                        :to="{ name: 'monitors' }"
                    >{{ $t('monitors') }}</v-tab>
                    <v-tab
                        :to="{ name: 'outages' }"
                    >{{ $t('outages') }}</v-tab>
                    <v-tab
                        v-if="hasFeature('custom-reports')"
                        :to="{ name: 'reports' }"
                    >{{ $t('reports') }}</v-tab>
                    <v-tab
                        v-if="hasFeature('status-page')"
                        :to="{ name: 'status-pages' }"
                    >{{ $t('status_page') }}</v-tab>
                    <v-spacer></v-spacer>
                    <v-menu bottom offset-y>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                text
                                class="align-self-center mr-4"
                                v-on="on"
                            >
                                {{ $t('account') }}
                                <v-icon right>arrow_drop_down</v-icon>
                            </v-btn>
                        </template>
                        <account-menu></account-menu>
                    </v-menu>
                </v-tabs>
            </template>
        </v-app-bar>

        <!--
        <new-feature
            v-if="isAuthenticated && isDesktop && changelog"
        ></new-feature>
        -->

        <v-main>
            <v-container fluid fill-height>
                <v-dialog v-model="hasModalLoader" persistent width="300">
                    <v-card color="primary" dark>
                        <v-card-text>
                        <p class="pt-4 pb-2" v-html="modalLoader"></p>
                        <v-progress-linear indeterminate color="white" class="mb-0">
                        </v-progress-linear>
                        </v-card-text>
                    </v-card>
                </v-dialog>

                <router-view></router-view>
            </v-container>
        </v-main>
        <footer v-if="showFooter">
            Admin {{ version }} &mdash;
            <a href="https://monitive.com/changelog/" target="_blank">Changelog</a> &mdash;
            <a href="https://staging-status.monitive.com/monitive-services" target="_blank">Service status</a> &mdash;
            <a href="https://staging-status.monitive.com/node-locations" target="_blank">Network status</a> &mdash;
            Support via <a href="https://wa.me/40740828822" target="_blank">Whatsapp</a>,
            <a href="mailto:support@monitive.com">Email</a> <!-- <span
                v-if="currentTeam && currentTeam.meta && currentTeam.meta.plan
                    && currentTeam.meta.plan === 'free'"
            >&mdash;<a href="https://monitive.com/bfs/" class="highlight">50% OFF Yearly Plans</a></span> -->
        </footer>
        <div :class="demoFloaterClass" v-if="showDemo">
            <div class="demo-floater__content">
                <div class="text-h4 pb-2">Demo Account</div>
                This is a real, read-only, demo account with live monitors and reporting.<br />
                Feel free to browse around and sign up to monitor your own websites when
                you are ready.
            </div>
            <div class="demo-floater__cta">
                <v-btn :loading="demoSignup" large @click="startMonitoring">Start Monitoring</v-btn>
            </div>
        </div>

    <v-bottom-navigation
      v-if="isMobile"
      v-model="bottomNav"
      :value="true"
      :height="isStandalone ? 78 : 56"
      :background-color="isImpersonated ? 'amber' : 'primary'"
      class="d-flex"
      app
    >
      <v-btn
        value="dashboard" height="60" :to="{ name: 'dashboard' }"
      >
        <span>{{ $t('dashboard') }}</span>
        <v-icon>dashboard</v-icon>
      </v-btn>

      <v-btn
        value="monitors" height="60" :to="{ name: 'monitors' }"
      >
        <span>{{ $t('monitors') }}</span>
        <v-icon>list</v-icon>
      </v-btn>

      <v-btn
        value="outages" height="60" :to="{ name: 'outages' }"
      >
        <span>{{ $t('outages') }}</span>
        <v-icon>warning</v-icon>
      </v-btn>

      <v-btn
        value="account" height="60" :to="{ name: 'account' }"
      >
        <span>{{ $t('account') }}</span>
        <v-icon>person</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <v-snackbar
      v-model="snackbar"
      bottom right vertical app
      :timeout="8000"
    >
        <!-- @link https://github.com/vuetifyjs/vuetify/issues/7310 -->
        <div v-show="false" class="v-menu__content--active" />
        {{ text }}
        <template v-slot:action="{ attrs }">
            <v-btn
                color="red"
                text
                v-bind="attrs"
                @click="snackbar = false"
            >
                {{ $t('close') }}
            </v-btn>
        </template>
    </v-snackbar>
    <v-snackbar v-model="updateAvailable" :top="true" :timeout="20000" elevation="12"
    >
      <!-- @link https://github.com/vuetifyjs/vuetify/issues/7310 -->
      <div v-show="false" class="v-menu__content--active" />
      {{ $t('newVersionAvailable') }}
      <v-btn color="green" text @click="refreshApplication()">
        {{ $t('reload') }}
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Workbox } from 'workbox-window';
import Pusher from 'pusher-js';
import Detect from '@/plugins/detect';

function logPusher(message) {
    console.log(
        `%c PUSHER %c ${message}`,
        'font-weight: bold; color: #00BFFF; background: #333;',
        'color: inherit; background: inherit;',
    );
}

export default {
    name: 'app',

    data: () => ({
        privateChannel: '',
        bottomNav: 'dashboard',
        fab: true,
        snackbar: false,
        updateAvailable: false,
        text: '',
        toolbar: false,
        drawer: null,
        right: null,
        showDemo: false,
        demoSignup: false,
    }),

    watch: {
        // eslint-disable-next-line
        'currentTeam.id': function () {
            if (this.currentTeam) {
                this.registerPusher();
            }
        },
        // eslint-disable-next-line
        'currentUser.attributes': function () {
            if (this.currentUser && this.currentUser.attributes && this.currentUser.attributes.email
                && this.currentUser.attributes.email === process.env.VUE_APP_DEMO_EMAIL) {
                this.showDemo = true;
            } else {
                this.showDemo = false;
            }
        },
    },

    mounted() {
        window.addEventListener('unhandledrejection', this.unhandledRejection);
        this.$vuetify.lang.current = this.$root.$i18n.locale;
        this.$root.$on('show-snackbar', (snack) => {
            this.text = snack.message;
            this.snackbar = true;
        });

        this.checkApiConnection();

        this.checkUpdate();
        console.log('this.currentUser', this.currentUser);
    },

    beforeDestroy() {
        // remove on unmount
        window.removeEventListener('unhandledrejection', this.unhandledRejection);
    },

    methods: {
        ...mapActions('team', { loadTeam: 'load' }),
        ...mapActions('monitor', {
            loadPending: 'loadPending',
            loadDown: 'loadDown',
            reloadMonitor: 'loadById',
        }),
        registerPusher() {
            // not the proper way to get the currentTeam, needs refactoring
            const teamId = this.currentTeam.id;
            if (!teamId) {
                logPusher('Not registering Pusher as we do not have a team ID.');
                return false;
            }
            this.privateChannel = `private-App.Team.${teamId}`;
            logPusher(`Registering Pusher on team channel ${this.privateChannel}`);

            const pusher = new Pusher(global.pusherKey, {
                cluster: 'eu',
                authEndpoint: `${process.env.VUE_APP_API_ENDPOINT}/broadcast-auth`,
                auth: {
                    headers: {
                        Authorization: `Bearer ${this.accessToken}`,
                    },
                },
            });
            const channel = pusher.subscribe(this.privateChannel);

            channel.bind(
                'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
                (data) => {
                    console.log('%c PUSHER %c NOTIFICATION An event was triggered with message:', 'font-weight: bold; color: #00BFFF; background: #333;', 'color: inherit; background: inherit;', data.meta.notification.body);
                    if (data.data.type !== undefined && data.data.type === 'monitor') {
                        this.handleMonitorsNotification(data);
                    }
                },
            );

            return true;
        },

        handleMonitorsNotification(notification) {
            logPusher(`Monitors notification received for ID ${notification.data.id}`);
            this.loadTeam(this.currentTeam.id);
            this.reloadMonitor(notification.data.id);
            this.loadPending();
            this.loadDown();
        },

        feedback() {
            if (this.$root.$i18n.locale === 'es') {
                return window.open('https://feedback.userreport.com/f04e5f1a-6c4d-4d76-a156-8d81a0c4ed07/');
            }
            return window.open('https://feedback.userreport.com/27972f8e-2c3a-4bff-8602-985a1b595880/');
        },

        checkApiConnection() {
            return axios.get(`${process.env.VUE_APP_API_ENDPOINT}/status`).then((result) => {
                console.log('checkApiConnection', result.data.meta.status);
                if (result.data.meta.status !== 'active') {
                    this.$root.$emit('show-snackbar', {
                        message: 'Maintenance mode, some data might be unavailable.',
                        timeout: 8000,
                    });
                }
            }).catch((error) => {
                console.log('checkApiConnection ERROR', error);
                this.$root.$emit('show-snackbar', {
                    message: `Could not connect to server: ${error}`,
                    timeout: 8000,
                });
            });
        },

        checkUpdate() {
            if ('serviceWorker' in navigator) {
                const wb = new Workbox('service-worker.js');

                wb.addEventListener('installed', (event) => {
                    if (event.isUpdate) {
                        this.updateAvailable = true;
                    }
                });

                wb.register();
            }
        },
        refreshApplication() {
            window.location.reload(true);
        },
        logout() {
            this.$router.push('/sign-out');
        },
        unhandledRejection(e) {
            if (/loading chunk [a-z0-9-]+ failed./i.test(e.reason) && navigator.onLine) {
                if (window.Bugsnag) {
                    window.Bugsnag.notify(new Error('App Chunk Error, reloading...'));
                }
                setTimeout(() => { window.location.reload(); }, 500);
            }
        },
        startMonitoring() {
            this.demoSignup = true;
            this.$router.push({ name: 'demo-signup' });
            this.$root.gaEngagement('demo_signup');
        },
    }, // §.methods

    computed: {
        // ...mapGetters('changelog', { changelog: 'changelog' }),
        ...mapGetters('ui', {
            contextualMenu: 'contextualMenu',
            navActions: 'navActions',
            modalLoader: 'modalLoader',
        }),
        ...mapGetters('user', { currentUser: 'current', accessToken: 'accessToken' }),
        ...mapGetters('team', { currentTeam: 'current', hasFeature: 'hasFeature' }),
        ...mapGetters([
            'actionDisabled',
            'isAdmin',
        ]),
        firstName() {
            if (!this.currentUser || !this.currentTeam) {
                return '';
            }

            return this.currentUser.attributes.firstName;
        },
        deviceClass() {
            return Detect.isMobile() ? 'mobile-device' : 'desktop-device';
        },
        isDesktop() {
            return this.deviceClass === 'desktop-device' && this.firstName !== '';
        },
        isMobile() {
            return this.deviceClass !== 'desktop-device' && this.firstName !== '';
        },
        appClass() {
            return `${this.deviceClass} ${this.isAuthenticated ? 'auth-app' : 'guest-app'}`;
        },
        hasModalLoader() {
            return !!this.modalLoader;
        },
        isStandalone() {
            return ('standalone' in window.navigator) && window.navigator.standalone;
        },
        isAuthenticated() {
            return this.currentUser || this.currentTeam;
        },
        isImpersonated() {
            return typeof global.impersonation !== 'undefined' && global.impersonation;
        },
        version() {
            return process.env.VUE_APP_VERSION;
        },
        showFooter() {
            return this.deviceClass === 'desktop-device'; // || !this.isMobile
        },
        demoFloaterClass() {
            return `demo-floater ${this.showDemo ? 'demo-floater--visible' : ''} ${Detect.isMobile() ? 'demo-floater--mobile' : ''}`;
        },
    }, // §.computed
    components: {
        AccountMenu: () => import('@/components/AccountMenu.vue'),
        // NewFeature: () => import('@/components/NewFeature.vue'),
    },
};
/* eslint-disable */
</script>

<style lang="scss">
@import 'sass/generic';

.theme--light {
    &.v-app-bar.v-toolbar.v-sheet {
        background-color: $brand-pink !important;
    }
}

div.v-application.theme--light {
    .v-application--wrap {
        background: #F0F0F0;

        .v-app-bar.v-toolbar {
            color: #FFFFFF;

            .v-tab--active {
                color: #FFFFFF;
            }
        }
    }

    &.guest-app {
        .v-application--wrap {
            background: $brand-pink;
        }
    }

    .v-card.v-sheet, .v-data-table {
        background-color: #FCFCFC;
        box-shadow:
            0px 3px 1px -2px rgba(128, 128, 128, 0.1),
            0px 2px 2px 0px rgba(128, 128, 128, 0.1),
            0px 1px 5px 0px rgba(128, 128, 128, 0.1);
    }

    .v-item-group.v-bottom-navigation {
        box-shadow: none;

        .v-btn--active {
            color: #ffffff;
        }
        .v-btn__content {
            span {
                font-weight: 400;
            }
        }
    }
}

.demo-floater {
    // position: absolute;
    background-color: var(--primary);
    color: var(--light-grey);
    bottom: -10rem;
    left: 0;
    right: 0;
    padding: 2rem;
    transition: bottom 2s ease-out;
    display: flex;

    &--visible {
        bottom: 0;
    }

    &--mobile {
        flex-direction: column;
        .demo-floater__cta {
            padding: 1rem 0 3rem;
        }
    }

    &__content {
        flex: 1 1;
    }

    &__cta {
        align-self: center;
    }
}

</style>
